import {
    Users,
    User,
    ChevronRight,
    ChevronDown,
    Building2,
    Tag,
    Link2,
    Plus,
    X,
    type LucideIcon,
} from 'lucide-react'
import {useParams} from 'react-router-dom'
import {useMemo, useState} from 'react'
import {useMutation, useQuery, useQueryClient} from '@tanstack/react-query'
import {useI18n} from '@/i18n'
import {Stack} from '@/components/shared/Stack'
import {Strong} from '@/components/shared/Strong'
import {StatBox} from '@/components/shared/StatBox'
import {routes} from '@/lib/routes'
import {notFoundIfMissing} from '@/lib/not-found-error'
import {Badge, BadgeVariant} from '@/components/ui/badge'
import {Button} from '@/components/ui/button'
import {
    Table,
    TableBody,
    TableCell,
    TableHead,
    TableHeader,
    TableRow,
} from '@/components/ui/table'
import {
    Dialog,
    DialogContent,
    DialogDescription,
    DialogFooter,
    DialogHeader,
    DialogTitle,
} from '@/components/ui/dialog'
import {Combobox, type ComboboxOption} from '@/components/ui/combobox'
import type {PartyRelationship, PartyRelationshipSchemaEntry} from '@/types/party'
import {IdentityLifecycleStateBadge} from '@/types/identity'
import {useAppConfig, labelOf} from '@/hooks/use-app-config'
import {formatDateOnly, formatTimestamp} from '@/lib/date-utils'
import type {TypeValue} from '@/types/types'
import {usePromotedTypes, findPromoted, PROMOTABLE_ENTITY_TABLES} from '@/hooks/use-promoted-types'
import {CONCEPT_ICON, type ConceptKey} from '@/lib/concepts'
import {InlineAttributeEditor} from '@/components/attributes/InlineAttributeEditor'
import {SourcesCard, SourcesEntityType} from '@/components/sources/SourcesCard'
import {AttributeEntityType} from '@/types/attribute-entity-type'
import {resolveEntityTypeIcon, resolveTypeValueIcon} from '@/components/entity-type/entity-type-icon'
import {PageLayout} from '@/components/shared/PageLayout'
import {SeverityBadge} from '@/components/violations/SeverityBadge'
import {ViolationSubjectTable} from '@/types/violation'
import {CardSection} from '@/components/shared/CardSection'
import {ClickableTableRow} from '@/components/shared/ClickableTableRow'
import {TextLink} from '@/components/shared/TextLink'
import {MutedText} from '@/components/shared/MutedText'
import {TreeRow} from '@/components/shared/TreeRow'
import {Icon} from '@/components/shared/Icon'
import {InlineRow} from '@/components/shared/InlineRow'
import {usePartyDetail, PARTY_KEY, PARTY_SUBKEY_PARENT_RELATIONSHIPS, PARTY_SUBKEY_CHILD_RELATIONSHIPS} from '@/features/party/hooks/usePartyDetail'
import {usePartyPermissions} from '@/features/party/hooks/usePartyPermissions'
import {useServices} from '@/services/context'
import {toast} from '@/hooks/use-toast'


function resolveIcon(name: string | null): LucideIcon {
    return resolveEntityTypeIcon(name) ?? Link2
}


const COLLAPSE_THRESHOLD = 5

interface AddRelationshipDialogProps {
    open: boolean
    onOpenChange: (open: boolean) => void
    schemaEntry: PartyRelationshipSchemaEntry
    side: 'from' | 'to'
    currentPartyId: string
    label: string
}

function AddRelationshipDialog({
    open, onOpenChange, schemaEntry, side, currentPartyId, label,
}: AddRelationshipDialogProps) {
    const i18n = useI18n()
    const services = useServices()
    const queryClient = useQueryClient()
    const [search, setSearch] = useState('')
    const [selectedId, setSelectedId] = useState<string | null>(null)

    const counterpartType = side === 'from' ? schemaEntry.toPartyType : schemaEntry.fromPartyType

    const partiesQuery = useQuery({
        queryKey: ['party-rel-picker', schemaEntry.schemaEntryId, counterpartType, search],
        queryFn: async () => {
            const params: Record<string, string | number> = {size: 5, partyType: counterpartType}
            if (search.length > 0) params.q = search
            const page = await services.parties.list(params)
            return page.content ?? []
        },
        enabled: open,
    })

    const options: ComboboxOption[] = useMemo(() => {
        const parties = partiesQuery.data ?? []
        return parties
            .filter(p => p.id !== currentPartyId)
            .map(p => ({value: p.id, label: p.derivedDisplayName}))
    }, [partiesQuery.data, currentPartyId])

    const createMutation = useMutation({
        mutationFn: async () => {
            if (selectedId == null) throw new Error('No counterpart selected')
            const fromPartyId = side === 'from' ? currentPartyId : selectedId
            const toPartyId = side === 'from' ? selectedId : currentPartyId
            return services.partyRelationships.create({
                fromPartyId,
                toPartyId,
                relationshipType: schemaEntry.relationshipType,
            })
        },
        onSuccess: async () => {
            const otherEnd = selectedId
            await queryClient.invalidateQueries({queryKey: [PARTY_SUBKEY_PARENT_RELATIONSHIPS, currentPartyId]})
            await queryClient.invalidateQueries({queryKey: [PARTY_SUBKEY_CHILD_RELATIONSHIPS, currentPartyId]})
            if (otherEnd != null) {
                await queryClient.invalidateQueries({queryKey: [PARTY_SUBKEY_PARENT_RELATIONSHIPS, otherEnd]})
                await queryClient.invalidateQueries({queryKey: [PARTY_SUBKEY_CHILD_RELATIONSHIPS, otherEnd]})
            }
            toast({title: i18n.parties.relationshipCreateSuccess})
            setSelectedId(null)
            setSearch('')
            onOpenChange(false)
        },
    })

    const handleConfirm = () => {
        if (selectedId == null) return
        createMutation.mutate()
    }

    const handleOpenChange = (next: boolean) => {
        if (!next) {
            setSelectedId(null)
            setSearch('')
        }
        onOpenChange(next)
    }

    return (
        <Dialog open={open} onOpenChange={handleOpenChange}>
            <DialogContent data-testid={`dialog-add-relationship-${schemaEntry.schemaEntryId}`}>
                <DialogHeader>
                    <DialogTitle>{i18n.parties.addRelationship({label})}</DialogTitle>
                    <DialogDescription>{i18n.parties.relationshipPickerPlaceholder}</DialogDescription>
                </DialogHeader>
                <Combobox
                    options={options}
                    value={selectedId}
                    onValueChange={setSelectedId}
                    onSearch={setSearch}
                    isLoading={partiesQuery.isLoading}
                    placeholder={i18n.parties.relationshipPickerPlaceholder}
                    data-testid={`combobox-add-relationship-${schemaEntry.schemaEntryId}`}
                />
                <DialogFooter>
                    <Button
                        type="button"
                        variant="outline"
                        onClick={() => handleOpenChange(false)}
                        data-testid={`btn-cancel-add-relationship-${schemaEntry.schemaEntryId}`}
                    >
                        {i18n.common.cancel}
                    </Button>
                    <Button
                        type="button"
                        onClick={handleConfirm}
                        disabled={selectedId == null || createMutation.isPending}
                        data-testid={`btn-confirm-add-relationship-${schemaEntry.schemaEntryId}`}
                    >
                        {createMutation.isPending ? i18n.common.saving : i18n.common.save}
                    </Button>
                </DialogFooter>
            </DialogContent>
        </Dialog>
    )
}

interface RemoveRelationshipDialogProps {
    open: boolean
    onOpenChange: (open: boolean) => void
    relationship: PartyRelationship
    side: 'from' | 'to'
    currentPartyId: string
}

function RemoveRelationshipDialog({
    open, onOpenChange, relationship, side, currentPartyId,
}: RemoveRelationshipDialogProps) {
    const i18n = useI18n()
    const services = useServices()
    const queryClient = useQueryClient()
    const counterpart = side === 'from' ? relationship.toParty : relationship.fromParty

    const endMutation = useMutation({
        mutationFn: () => services.partyRelationships.end(relationship.id),
        onSuccess: async () => {
            const otherEnd = counterpart.id
            await queryClient.invalidateQueries({queryKey: [PARTY_SUBKEY_PARENT_RELATIONSHIPS, currentPartyId]})
            await queryClient.invalidateQueries({queryKey: [PARTY_SUBKEY_CHILD_RELATIONSHIPS, currentPartyId]})
            await queryClient.invalidateQueries({queryKey: [PARTY_SUBKEY_PARENT_RELATIONSHIPS, otherEnd]})
            await queryClient.invalidateQueries({queryKey: [PARTY_SUBKEY_CHILD_RELATIONSHIPS, otherEnd]})
            toast({title: i18n.parties.relationshipEndedSuccess})
            onOpenChange(false)
        },
    })

    return (
        <Dialog open={open} onOpenChange={onOpenChange}>
            <DialogContent data-testid={`dialog-remove-relationship-${relationship.id}`}>
                <DialogHeader>
                    <DialogTitle>{i18n.parties.removeRelationshipTitle}</DialogTitle>
                    <DialogDescription>
                        {i18n.parties.removeRelationshipConfirm({name: counterpart.displayName})}
                    </DialogDescription>
                </DialogHeader>
                <DialogFooter>
                    <Button
                        type="button"
                        variant="outline"
                        onClick={() => onOpenChange(false)}
                        data-testid={`btn-cancel-remove-relationship-${relationship.id}`}
                    >
                        {i18n.common.cancel}
                    </Button>
                    <Button
                        type="button"
                        variant="destructive"
                        onClick={() => endMutation.mutate()}
                        disabled={endMutation.isPending}
                        data-testid={`btn-confirm-remove-relationship-${relationship.id}`}
                    >
                        {endMutation.isPending ? i18n.common.saving : i18n.common.delete}
                    </Button>
                </DialogFooter>
            </DialogContent>
        </Dialog>
    )
}

interface AddTriggerProps {
    schemaEntryId: string
    label: string
    disabled: boolean
    onClick: () => void
}

function AddTrigger({schemaEntryId, label, disabled, onClick}: AddTriggerProps) {
    const i18n = useI18n()
    const ariaLabel = disabled
        ? i18n.parties.addRelationshipMaxReached({label})
        : i18n.parties.addRelationship({label})
    return (
        <Button
            type="button"
            variant="ghost"
            size="icon-sm"
            data-testid={`btn-add-relationship-${schemaEntryId}`}
            onClick={onClick}
            disabled={disabled}
            title={ariaLabel}
            aria-label={ariaLabel}
        >
            <Icon icon={Plus}/>
        </Button>
    )
}

interface RemoveTriggerProps {
    relationshipId: string
    counterpartName: string
    onClick: () => void
}

function RemoveTrigger({relationshipId, counterpartName, onClick}: RemoveTriggerProps) {
    const i18n = useI18n()
    const ariaLabel = i18n.parties.removeRelationship({name: counterpartName})
    return (
        <Button
            type="button"
            variant="ghost"
            size="icon-sm"
            data-testid={`btn-remove-relationship-${relationshipId}`}
            onClick={onClick}
            title={ariaLabel}
            aria-label={ariaLabel}
        >
            <Icon icon={X}/>
        </Button>
    )
}

function RelationshipSection({
                                 icon: SectionIcon,
                                 label,
                                 relationships,
                                 side,
                                 partyTypes,
                                 schemaEntry,
                                 currentPartyId,
                                 canCreate,
                                 canEnd,
                             }: {
    icon: LucideIcon
    label: string
    relationships: PartyRelationship[]
    side: 'from' | 'to'
    partyTypes: TypeValue[]
    schemaEntry: PartyRelationshipSchemaEntry
    currentPartyId: string
    canCreate: boolean
    canEnd: boolean
}) {
    const i18n = useI18n()
    const [sectionOpen, setSectionOpen] = useState(relationships.length <= 1)
    const [showAll, setShowAll] = useState(false)
    const [addDialogOpen, setAddDialogOpen] = useState(false)
    const [removeTarget, setRemoveTarget] = useState<PartyRelationship | null>(null)

    const getParty = (rel: PartyRelationship) =>
        side === 'from' ? rel.toParty : rel.fromParty

    // WHY: max_from_count caps how many active edges a single FROM party can
    // have for this (relationship_type, to_party_type) — the trigger
    // {@code enforce_party_relationship_schema()} counts rows where
    // pr.from_party_id = NEW.from_party_id. max_to_count is the symmetric cap
    // on a single TO party's incoming edges. So when the current party is on
    // the FROM side we read max_from_count, and on the TO side we read
    // max_to_count. A value of 0 means unlimited.
    const sideMaxCount = side === 'from' ? schemaEntry.maxFromCount : schemaEntry.maxToCount
    const atMaxCount = sideMaxCount > 0 && relationships.length >= sideMaxCount
    const showAdd = canCreate

    const renderAddTrigger = () => {
        if (!showAdd) return null
        return (
            <AddTrigger
                schemaEntryId={schemaEntry.schemaEntryId}
                label={label}
                disabled={atMaxCount}
                onClick={() => setAddDialogOpen(true)}
            />
        )
    }

    const renderRemoveTrigger = (rel: PartyRelationship) => {
        if (!canEnd) return null
        return (
            <RemoveTrigger
                relationshipId={rel.id}
                counterpartName={getParty(rel).displayName}
                onClick={() => setRemoveTarget(rel)}
            />
        )
    }

    const renderDialogs = () => (
        <>
            {showAdd && (
                <AddRelationshipDialog
                    open={addDialogOpen}
                    onOpenChange={setAddDialogOpen}
                    schemaEntry={schemaEntry}
                    side={side}
                    currentPartyId={currentPartyId}
                    label={label}
                />
            )}
            {canEnd && removeTarget && (
                <RemoveRelationshipDialog
                    open={removeTarget !== null}
                    onOpenChange={(next) => { if (!next) setRemoveTarget(null) }}
                    relationship={removeTarget}
                    side={side}
                    currentPartyId={currentPartyId}
                />
            )}
        </>
    )

    if (relationships.length === 0) {
        return (
            <>
                <InlineRow gap="md" className="font-medium py-1">
                    <Icon icon={SectionIcon} muted/>
                    {label}
                    <MutedText>&mdash;</MutedText>
                    {renderAddTrigger()}
                </InlineRow>
                {renderDialogs()}
            </>
        )
    }

    if (relationships.length === 1) {
        const rel = relationships[0]
        const party = getParty(rel)
        return (
            <>
                <InlineRow gap="md" className="font-medium py-1">
                    <Icon icon={SectionIcon}/>
                    {label}
                    <TextLink
                        to={routes.identityManagement.parties.detail(party.id)}
                        testId={`link-rel-${party.id}`}
                        stopPropagation
                    >
                        {party.displayName}
                    </TextLink>
                    <Badge variant="outline" className="text-xs">
                        {labelOf(partyTypes, party.subType ?? '')}
                    </Badge>
                    {renderRemoveTrigger(rel)}
                    {renderAddTrigger()}
                </InlineRow>
                {renderDialogs()}
            </>
        )
    }

    const collapsible = relationships.length > COLLAPSE_THRESHOLD
    const visibleRelationships = collapsible && !showAll
        ? relationships.slice(0, COLLAPSE_THRESHOLD)
        : relationships

    return (
        <Stack gap="xs" className="py-1">
            <InlineRow gap="md">
                <Button
                    type="button"
                    variant="ghost"
                    size="sm"
                    data-testid={`btn-toggle-section-${label.toLowerCase().replace(/\s+/g, '-')}`}
                    onClick={() => setSectionOpen(!sectionOpen)}
                    className="justify-start"
                >
                    <Icon icon={SectionIcon}/>
                    {label}
                    <Badge variant="secondary" className="text-xs">
                        {relationships.length}
                    </Badge>
                    <Icon icon={sectionOpen ? ChevronDown : ChevronRight} muted/>
                </Button>
                {renderAddTrigger()}
            </InlineRow>
            {sectionOpen && (
                <Stack gap="xs" className="pl-6 mt-1">
                    {visibleRelationships.map(rel => {
                        const party = getParty(rel)
                        return (
                            <InlineRow key={rel.id} gap="md" className="py-0.5">
                                <TextLink
                                    to={routes.identityManagement.parties.detail(party.id)}
                                    testId={`link-rel-${party.id}`}
                                    stopPropagation
                                >
                                    {party.displayName}
                                </TextLink>
                                <Badge variant="outline" className="text-xs">
                                    {labelOf(partyTypes, party.subType ?? '')}
                                </Badge>
                                {renderRemoveTrigger(rel)}
                            </InlineRow>
                        )
                    })}
                    {collapsible && (
                        <Button
                            type="button"
                            variant="link"
                            size="sm"
                            data-testid={`btn-show-all-${label.toLowerCase().replace(/\s+/g, '-')}`}
                            onClick={() => setShowAll(!showAll)}
                            className="justify-start"
                        >
                            {showAll
                                ? i18n.common.showLess
                                : i18n.common.showMoreCount({count: relationships.length})}
                        </Button>
                    )}
                </Stack>
            )}
            {renderDialogs()}
        </Stack>
    )
}

function AncestorTreeNode({
                              ancestors,
                              currentPartyName,
                              partyTypes,
                              level = 0,
                          }: {
    ancestors: { id: string; derivedDisplayName: string; partyType: string }[]
    currentPartyName: string
    partyTypes: TypeValue[]
    level?: number
}) {
    const ancestor = ancestors[0]
    if (!ancestor) {
        return (
            <TreeRow depth={level} variant="highlighted">
                <Strong size="sm" weight="semibold" variant="primary" className="ml-6">{currentPartyName}</Strong>
            </TreeRow>
        )
    }
    return (
        <div>
            <TreeRow depth={level} variant="hoverable">
                <Icon icon={ChevronDown} muted className="mr-1"/>
                <TextLink
                    to={routes.identityManagement.parties.detail(ancestor.id)}
                    testId={`link-ancestor-${ancestor.id}`}
                    stopPropagation
                >
                    {ancestor.derivedDisplayName}
                </TextLink>
                <Badge variant="outline" className="ml-2 text-xs">
                    {labelOf(partyTypes, ancestor.partyType)}
                </Badge>
            </TreeRow>
            <AncestorTreeNode
                ancestors={ancestors.slice(1)}
                currentPartyName={currentPartyName}
                partyTypes={partyTypes}
                level={level + 1}
            />
        </div>
    )
}

function TreeSection({
                         ancestors,
                         currentPartyName,
                         partyTypes,
                     }: {
    ancestors: { id: string; derivedDisplayName: string; partyType: string }[]
    currentPartyName: string
    partyTypes: TypeValue[]
}) {
    const i18n = useI18n()
    const [sectionOpen, setSectionOpen] = useState(false)

    const immediateParent = ancestors[0]

    if (!immediateParent) return null

    const topDown = [...ancestors].reverse()

    return (
        <Stack gap="xs" className="py-1">
            <InlineRow gap="md" className="font-medium">
                <Icon icon={Building2}/>
                {i18n.parties.partyHierarchy}
                <TextLink
                    to={routes.identityManagement.parties.detail(immediateParent.id)}
                    testId={`link-org-${immediateParent.id}`}
                    stopPropagation
                >
                    {immediateParent.derivedDisplayName}
                </TextLink>
                <Badge variant="outline" className="text-xs">
                    {labelOf(partyTypes, immediateParent.partyType)}
                </Badge>
                <Button
                    type="button"
                    variant="ghost"
                    size="icon-sm"
                    data-testid="btn-toggle-org-tree"
                    onClick={() => setSectionOpen(!sectionOpen)}
                >
                    <Icon icon={sectionOpen ? ChevronDown : ChevronRight}/>
                </Button>
            </InlineRow>
            {sectionOpen && (
                <Stack gap="xs" className="pl-6 mt-2">
                    <StatBox variant="plain" className="bg-muted/20">
                        <AncestorTreeNode
                            ancestors={topDown}
                            currentPartyName={currentPartyName}
                            partyTypes={partyTypes}
                        />
                    </StatBox>
                </Stack>
            )}
        </Stack>
    )
}

interface RelationshipSectionConfig {
    key: string
    icon: LucideIcon
    label: string
    relationships: PartyRelationship[]
    side: 'from' | 'to'
    schemaEntry: PartyRelationshipSchemaEntry
}

export function PartyDetail() {
    const i18n = useI18n()
    const {id} = useParams<{ id: string }>()
    const {canUpdate, canCreateRelationship, canEndRelationship} = usePartyPermissions()

    const {
        party,
        identities,
        parentRelationships,
        childRelationships,
        ancestors,
        isLoading: partyLoading,
        error: partyError,
    } = usePartyDetail(id)

    const {partyTypes, identityTypes, lifecycleStates, partyRelationshipSchema, partyRelationshipTypes} = useAppConfig()
    const {promoted} = usePromotedTypes()
    const promotedIcon = useMemo(
        () => party?.partyType
            ? findPromoted(promoted, PROMOTABLE_ENTITY_TABLES.PARTY_TYPES, party.partyType)
            : undefined,
        [promoted, party?.partyType],
    )

    const relationshipSections: RelationshipSectionConfig[] = useMemo(() => {
        if (!party) return []
        const sections: RelationshipSectionConfig[] = []

        for (const entry of partyRelationshipSchema) {
            if (entry.fromPartyType === party.partyType && entry.fromSideDisplay) {
                const rels = parentRelationships.filter(
                    r => r.relationshipType === entry.relationshipType
                        && r.toParty.subType === entry.toPartyType
                        && r.validTo == null,
                )
                const label = entry.fromSideLabel
                    || labelOf(partyRelationshipTypes, entry.relationshipType)
                sections.push({
                    key: `from-${entry.fromPartyType}-${entry.relationshipType}-${entry.toPartyType}`,
                    icon: resolveIcon(entry.iconFrom),
                    label,
                    relationships: rels,
                    side: 'from',
                    schemaEntry: entry,
                })
            }
            if (entry.toPartyType === party.partyType && entry.toSideDisplay) {
                const rels = childRelationships.filter(
                    r => r.relationshipType === entry.relationshipType
                        && r.fromParty.subType === entry.fromPartyType
                        && r.validTo == null,
                )
                const label = entry.toSideLabel
                    || labelOf(partyRelationshipTypes, entry.relationshipType)
                sections.push({
                    key: `to-${entry.fromPartyType}-${entry.relationshipType}-${entry.toPartyType}`,
                    icon: resolveIcon(entry.iconTo),
                    label,
                    relationships: rels,
                    side: 'to',
                    schemaEntry: entry,
                })
            }
        }

        return sections
    }, [party, partyRelationshipSchema, parentRelationships, childRelationships, partyRelationshipTypes])

    const HeaderIcon = useMemo(() => {
        const promotedConceptKey = promotedIcon?.icon as ConceptKey | undefined
        if (promotedConceptKey && CONCEPT_ICON[promotedConceptKey]) {
            return CONCEPT_ICON[promotedConceptKey]
        }
        return resolveTypeValueIcon(partyTypes, party?.partyType, User)
    }, [promotedIcon, partyTypes, party?.partyType])

    return (
        <PageLayout
            data-testid="party-detail-page"
            variant="detail"
            icon={HeaderIcon}
            title={party?.derivedDisplayName ?? ''}
            backTo={routes.identityManagement.parties.list()}
            loading={partyLoading}
            error={partyError ?? (!partyLoading ? notFoundIfMissing(party, 'PARTY') : undefined)}
            description={party ? (
                <InlineRow>
                    <Badge variant="outline">
                        {labelOf(partyTypes, party.partyType)}
                    </Badge>
                    <MutedText>
                        {i18n.parties.created} {formatTimestamp(party.createdAt)}
                    </MutedText>
                    <SeverityBadge entityTable={ViolationSubjectTable.PARTIES} entityId={party.id}/>
                </InlineRow>
            ) : undefined}
        >
            {!party ? null : <>
            {/* Identities Section */}
            <CardSection
                data-testid="section-identities"
                icon={Users}
                title={i18n.identities.title as string}
                description={i18n.parties.identityCount({count: identities.length}) as string}
            >
                {identities.length > 0 ? (
                    <Table>
                        <TableHeader>
                            <TableRow>
                                <TableHead data-testid="col-type">
                                    {i18n.common.type}
                                </TableHead>
                                <TableHead data-testid="col-lifecycle-state">
                                    {i18n.identities.lifecycleState}
                                </TableHead>
                                <TableHead data-testid="col-created-at">{i18n.common.createdAt}</TableHead>
                            </TableRow>
                        </TableHeader>
                        <TableBody>
                            {identities.map((identity) => (
                                <ClickableTableRow
                                    key={identity.id}
                                    to={routes.identityManagement.identities.detail(identity.id)}
                                >
                                    <TableCell>
                                        <TextLink
                                            to={routes.identityManagement.identities.detail(identity.id)}
                                            testId={`link-identity-${identity.id}`}
                                            stopPropagation
                                            variant="bold"
                                        >
                                            {labelOf(identityTypes, identity.identityType)}
                                        </TextLink>
                                    </TableCell>
                                    <TableCell>
                                        {(() => {
                                            const lifecycleBadge = IdentityLifecycleStateBadge.of(identity.lifecycleState)
                                            const StateIcon = lifecycleBadge?.icon
                                            return (
                                                <Badge variant={lifecycleBadge?.variant ?? BadgeVariant.OUTLINE} className="gap-1">
                                                    {StateIcon && <StateIcon className="h-3 w-3" aria-hidden="true"/>}
                                                    {labelOf(lifecycleStates, identity.lifecycleState)}
                                                </Badge>
                                            )
                                        })()}
                                    </TableCell>
                                    <TableCell>
                                        <MutedText>{formatDateOnly(identity.createdAt)}</MutedText>
                                    </TableCell>
                                </ClickableTableRow>
                            ))}
                        </TableBody>
                    </Table>
                ) : (
                    <MutedText block centered className="py-4">{i18n.parties.noIdentitiesLinked}</MutedText>
                )}
            </CardSection>

            {/* Sources Section — Spec §6.1 (572.07) */}
            <SourcesCard
                entityType={SourcesEntityType.parties}
                entityId={id!}
                canEdit={canUpdate}
                invalidateOnMutate={[[PARTY_KEY, id]]}
            />

            {/* Attributes */}
            <CardSection
                data-testid="section-attributes"
                icon={Tag}
                title={i18n.common.attributes as string}
            >
                <InlineAttributeEditor
                    entityType={AttributeEntityType.PARTY}
                    entityId={id!}
                    entityTypeCode={party.partyType}
                    canEdit={canUpdate}
                    sources={party.sources}
                />
            </CardSection>

            {/* Consolidated Relationships Card */}
            {(relationshipSections.length > 0 || ancestors.length > 0) && (
                <CardSection
                    data-testid="section-relationships"
                    icon={Link2}
                    title={i18n.parties.relationships as string}
                >
                    <Stack gap="xs">
                        {relationshipSections.map(section => (
                            <RelationshipSection
                                key={section.key}
                                icon={section.icon}
                                label={section.label}
                                relationships={section.relationships}
                                side={section.side}
                                partyTypes={partyTypes}
                                schemaEntry={section.schemaEntry}
                                currentPartyId={party.id}
                                canCreate={canCreateRelationship}
                                canEnd={canEndRelationship}
                            />
                        ))}
                    </Stack>
                </CardSection>
            )}

            {ancestors.length > 0 && (
                <CardSection
                    data-testid="section-party-hierarchy"
                    icon={Building2}
                    title={i18n.parties.partyHierarchy as string}
                >
                    <TreeSection
                        ancestors={ancestors}
                        currentPartyName={party.derivedDisplayName}
                        partyTypes={partyTypes}
                    />
                </CardSection>
            )}
            </>}
        </PageLayout>
    )
}
